// SubShell — mobile-first chrome for the subscriber PWA, harmonised to the
// operator design system: maroon top bar, SVG Icon tab bar (no emoji), bottom
// nav. Layout stays mobile (max 480px, sticky header, fixed bottom tabs).

import type { ReactElement, ReactNode } from 'react';
import { NavLink } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../auth.js';
import { Icon } from '../components/Icon.js';
import {
  current_language,
  font_class_for,
  locale_label,
  set_language,
  SUPPORTED_LOCALES,
  type Locale,
} from '../i18n/index.js';
import { NetworkStatus } from '../offline/NetworkStatus.js';

const TABS = [
  { to: '/sub', label_key: 'sub.tab_home' as const, Icon: Icon.home },
  { to: '/sub/chits', label_key: 'sub.tab_chits' as const, Icon: Icon.chits },
  { to: '/sub/history', label_key: 'sub.tab_history' as const, Icon: Icon.receipt },
  { to: '/sub/credit', label_key: 'sub.tab_credit' as const, Icon: Icon.shield },
] as const;

export function SubShell({ children }: { children: ReactNode }): ReactElement {
  const { t } = useTranslation();
  const { logout } = useAuth();
  const lang = current_language();

  return (
    <div className="sub-app">
      <header className="sub-top">
        <div className="brand">
          <div className={font_class_for(lang)}>{t('sub.brand', 'My Chits')}</div>
          <div className="tag">{t('app_name_en')}</div>
        </div>
        <div className="sub-top-actions">
          <NetworkStatus />
          <select value={lang} onChange={(e) => set_language(e.target.value as Locale)}>
            {SUPPORTED_LOCALES.map((l) => (
              <option key={l} value={l}>
                {locale_label(l)}
              </option>
            ))}
          </select>
          <button type="button" className="top-act" onClick={() => void logout()}>
            {t('actions.logout', 'Logout')}
          </button>
        </div>
      </header>

      <main className="sub-main">{children}</main>

      <nav className="sub-tabbar">
        {TABS.map((tab) => (
          <NavLink
            key={tab.to}
            to={tab.to}
            end={tab.to === '/sub'}
            className={({ isActive }) => 'sub-tab' + (isActive ? ' on' : '')}
          >
            <tab.Icon />
            <span className={font_class_for(lang)}>{t(tab.label_key)}</span>
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
